export type ReportStatus = 'OPEN' | 'IN_PROGRESS' | 'WAITING' | 'RESOLVED';

export type ResolutionCode = 'FIXED' | 'DISMISSED' | 'NO_ACTION';

export type AuditActionType =
    | 'DECISION'    // Governance decisions (e.g. changing severity)
    | 'ASSIGNMENT'  // Ownership changes
    | 'EXTERNAL'    // Communication with external agencies
    | 'CITIZEN'     // Public feedback
    | 'SYSTEM'      // Automated actions
    | 'STATUS'      // State transitions
    | 'INFO';       // General logging

export interface AuditLogEntry {
    action: AuditActionType;
    details: string;
    resolutionCode?: ResolutionCode;
}

/**
 * Formats a semantic comment string for the database.
 * Format: "[ACTION] Details"
 */
export function formatAuditComment(entry: AuditLogEntry): string {
    const prefix = `[${entry.action}]`;
    let details = entry.details;

    // Auto-prepend resolution code if present (and not already in details)
    if (entry.resolutionCode && !details.startsWith(`[${entry.resolutionCode}]`)) {
        details = `[${entry.resolutionCode}] ${details}`;
    }

    return `${prefix} ${details}`;
}

/**
 * Validates if a status transition is allowed.
 * Returns true if valid, throws error if invalid.
 */
export function validateStateTransition(current: ReportStatus, next: ReportStatus): boolean {
    // Simple state machine
    if (current === next) return true; // No-op is valid

    // Preventing re-opening resolved cases (optional strict rule, can be relaxed)
    if (current === 'RESOLVED' && next !== 'RESOLVED') {
        // We allow re-opening for now, but it should be logged heavily
        return true;
    }

    return true;
}

/**
 * Helper to get human readable label for resolution
 */
export function getResolutionLabel(code: ResolutionCode): string {
    switch (code) {
        case 'FIXED': return 'Utbedret';
        case 'DISMISSED': return 'Avvist';
        case 'NO_ACTION': return 'Ingen tiltak';
    }
}
